<template>
  <div class="update-section-container">
    <div v-if="isLoading" class="loading-state">
      <div class="loading-shimmer"></div>
    </div>

    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="fetchSectionData" class="retry-button">Retry</button>
    </div>

    <div v-else-if="showSuccess" class="success-message">
      <div class="success-icon">
        <i class="fas fa-check-circle"></i>
      </div>
      <h3>Section Updated Successfully!</h3>
      <p>The homepage section has been updated.</p>
      <div class="success-actions">
        <button @click="resetForm" class="update-again-button">Update Again</button>
        <button @click="viewSection" class="view-section-button">View Section</button>
      </div>
    </div>

    <form v-else @submit.prevent="updateSection" class="update-form">
      <div class="form-group">
        <label for="title">Title (English)</label>
        <input 
          type="text" 
          id="title" 
          v-model="formData.title" 
          class="form-control"
          required
        >
      </div>

      <div class="form-group">
        <label for="title_ar">Title (Arabic)</label>
        <input 
          type="text" 
          id="title_ar" 
          v-model="formData.title_ar" 
          class="form-control rtl"
          dir="rtl"
        >
      </div>

      <div class="form-group">
        <label for="subtitle">Subtitle (English)</label>
        <input 
          type="text" 
          id="subtitle" 
          v-model="formData.subtitle" 
          class="form-control"
        >
      </div>

      <div class="form-group">
        <label for="subtitle_ar">Subtitle (Arabic)</label>
        <input 
          type="text" 
          id="subtitle_ar" 
          v-model="formData.subtitle_ar" 
          class="form-control rtl"
          dir="rtl"
        >
      </div>

      <div class="form-group">
        <label for="type">Section Type</label>
        <select 
          id="type" 
          v-model="formData.type" 
          class="form-control"
          required
        >
          <option value="Banner">Banner</option>
          <option value="FullCardCarousel">Full Card Carousel</option>
          <option value="SquareCardCarouselV1">Square Card Carousel V1</option>
          <option value="SquareCardCarouselV2">Square Card Carousel V2</option>
          <option value="TallCardCarousel">Tall Card Carousel</option>
          <option value="GridCarousel">Grid Carousel</option>
        </select>
      </div>

      <div class="form-group">
        <label for="method_name">Method Name</label>
        <input 
          type="text" 
          id="method_name" 
          v-model="formData.method_name" 
          class="form-control"
          required
        >
      </div>

      <div class="form-group">
        <label for="cache_key">Cache Key</label>
        <input 
          type="text" 
          id="cache_key" 
          v-model="formData.cache_key" 
          class="form-control"
          required
        >
      </div>

      <div class="form-group">
        <label for="ttl">TTL (seconds)</label>
        <input 
          type="number" 
          id="ttl" 
          v-model.number="formData.ttl" 
          class="form-control"
          required
          min="0"
        >
      </div>

      <div class="form-group">
        <label for="order">Display Order</label>
        <input 
          type="number" 
          id="order" 
          v-model.number="formData.order" 
          class="form-control"
          required
          min="1"
        >
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-container">
          <input 
            type="checkbox" 
            id="is_active" 
            v-model="formData.is_active"
          >
          <span class="checkmark"></span>
          Active
        </label>
      </div>

      <div class="form-actions">
        <button type="button" @click="resetForm" class="cancel-button">Reset</button>
        <button type="submit" class="submit-button" :disabled="isSubmitting">
          <span v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin"></i> Updating...
          </span>
          <span v-else>Update Section</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

export default {
  name: 'UpdateHomepageSection',
  props: {
    sectionId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['section-updated'],
  setup(props, { emit }) {
    const router = useRouter();
    const isLoading = ref(true);
    const error = ref(null);
    const isSubmitting = ref(false);
    const showSuccess = ref(false);
    
    const formData = reactive({
      title: '',
      title_ar: '',
      subtitle: '',
      subtitle_ar: '',
      type: '',
      method_name: '',
      cache_key: '',
      ttl: 3600,
      order: 1,
      is_active: true
    });
    
    const originalData = reactive({});
    
    const fetchSectionData = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.get(`https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          const sectionData = response.data.data;
          
          // Update form data with section data
          formData.title = sectionData.title || '';
          formData.title_ar = sectionData.title_ar || '';
          formData.subtitle = sectionData.subtitle || '';
          formData.subtitle_ar = sectionData.subtitle_ar || '';
          formData.type = sectionData.type || '';
          formData.method_name = sectionData.method_name || '';
          formData.cache_key = sectionData.cache_key || '';
          formData.ttl = sectionData.ttl || 3600;
          formData.order = sectionData.order || 1;
          formData.is_active = sectionData.is_active || false;
          
          // Store original data for reset functionality
          Object.assign(originalData, formData);
        } else {
          error.value = 'Failed to load section data: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error fetching section data:', err);
        error.value = 'Failed to load section data: ' + (err.message || 'Network error');
      } finally {
        isLoading.value = false;
      }
    };
    
    const updateSection = async () => {
      isSubmitting.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token') ;
        
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/homepage-sections/${props.sectionId}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.success) {
          showSuccess.value = true;
          emit('section-updated', response.data.data);
        } else {
          error.value = 'Failed to update section: ' + (response.data.message || 'Unknown error');
        }
      } catch (err) {
        console.error('Error updating section:', err);
        error.value = 'Failed to update section: ' + (err.message || 'Network error');
      } finally {
        isSubmitting.value = false;
      }
    };
    
    const resetForm = () => {
      // Reset form data to original values
      Object.assign(formData, originalData);
      showSuccess.value = false;
    };
    
    const viewSection = () => {
      router.push(`/homepage/sections/${props.sectionId}`);
    };
    
    onMounted(() => {
      fetchSectionData();
    });
    
    return {
      isLoading,
      error,
      isSubmitting,
      showSuccess,
      formData,
      fetchSectionData,
      updateSection,
      resetForm,
      viewSection
    };
  }
};
</script>

<style scoped>
.update-section-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-state {
  height: 400px;
  background: #f1f5f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
  transform: translateX(-100%);
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.error-message {
  background: #FEE2E2;
  color: #B91C1C;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
}

.retry-button {
  background: #DC2626;
  color: white;
  border: none;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  margin-top: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #B91C1C;
}

.success-message {
  text-align: center;
  padding: 2rem;
}

.success-icon {
  font-size: 3rem;
  color: #10B981;
  margin-bottom: 1rem;
}

.success-message h3 {
  font-size: 1.5rem;
  color: #1F2937;
  margin-bottom: 0.5rem;
}

.success-message p {
  color: #6B7280;
  margin-bottom: 2rem;
}

.success-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

.update-again-button,
.view-section-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.update-again-button {
  background: #F3F4F6;
  color: #4B5563;
}

.update-again-button:hover {
  background: #E5E7EB;
}

.view-section-button {
  background: #3B82F6;
  color: white;
}

.view-section-button:hover {
  background: #2563EB;
}

.update-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.checkbox-group {
  flex-direction: row;
  align-items: center;
  margin-top: 0.5rem;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4B5563;
  margin-bottom: 0.5rem;
}

.form-control {
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1F2937;
  transition: border-color 0.2s;
}

.form-control:focus {
  border-color: #3B82F6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.rtl {
  text-align: right;
}

.checkbox-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  color: #4B5563;
  position: relative;
  padding-left: 30px;
  margin-bottom: 0;
}

.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #F3F4F6;
  border: 1px solid #D1D5DB;
  border-radius: 4px;
}

.checkbox-container:hover input ~ .checkmark {
  background-color: #E5E7EB;
}

.checkbox-container input:checked ~ .checkmark {
  background-color: #3B82F6;
  border-color: #3B82F6;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

.checkbox-container .checkmark:after {
  left: 7px;
  top: 3px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.form-actions {
  grid-column: span 2;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-button,
.submit-button {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.cancel-button {
  background: #F3F4F6;
  color: #4B5563;
}

.cancel-button:hover {
  background: #E5E7EB;
}

.submit-button {
  background: #3B82F6;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.submit-button:hover {
  background: #2563EB;
}

.submit-button:disabled {
  background: #93C5FD;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .update-section-container {
    padding: 1.5rem;
  }
  
  .update-form {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    grid-column: span 1;
  }
}
</style> 